<script setup lang="ts">
import { computed, ref } from 'vue';
import { NxtSelect, NxtIconButton } from '@future-harry/nxt-styles';
import type { AllowedCompany } from '@/core/collections/job-boards/job-board.interface';

const selectedCompanies = defineModel<AllowedCompany[]>({ required: true });

const lastSearchResults = ref<Array<{ id: number; name: string; logo_url?: string }>>([]);

const displayCompanies = computed(() => {
   return selectedCompanies.value.map((sc) => ({
      id: sc.id,
      name: sc.name,
   }));
});

async function customSearch(searchValue: string) {
   if (!searchValue || searchValue.trim().length < 2) return [];

   const { data } = await useFetch<any>(`/customers?filter[search]=${searchValue}`);

   const searchResults =
      data.value?.data?.map((company: any) => ({
         id: company.id,
         name: company.name,
      })) || [];

   lastSearchResults.value = searchResults;

   return searchResults;
}

function addCompany(id: number | unknown) {
   if (selectedCompanies.value.some((sc) => sc.id === id)) return;

   const companyData = lastSearchResults.value.find((c) => c.id === id);
   if (!companyData) return;

   selectedCompanies.value = [
      ...selectedCompanies.value,
      {
         id: companyData.id,
         name: companyData.name,
      },
   ];
}

function removeCompany(id: number) {
   if (!selectedCompanies.value) return;
   selectedCompanies.value = selectedCompanies.value.filter((sc) => sc.id !== id);
}
</script>

<template>
   <div class="company-selector">
      <div>
         <NxtSelect
            :name="'company-selector'"
            :label="$t('components.company-selector.add-label')"
            search-type="custom"
            :search-on-open="false"
            :has-clear-option="false"
            :custom-search="customSearch"
            :placeholder="$t('components.company-selector.placeholder')"
            @update:model-value="addCompany"
         />
      </div>

      <div v-if="displayCompanies.length" class="selected-companies">
         <div v-for="company in displayCompanies" :key="company.id" class="company-selector__item">
            <div class="company-info">
               <span>{{ company.name }}</span>
            </div>
            <NxtIconButton
               :name="'trash'"
               :color="'var(--nxt-dark)'"
               :background-color="'var(--nxt-transparent)'"
               :hover-color="'var(--nxt-light-grey)'"
               @on-click="removeCompany(company.id)"
            />
         </div>
      </div>
   </div>
</template>

<style scoped lang="scss">
.company-selector {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter-large);

   &__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: var(--nxt-gutter-small);

      .company-info {
         display: flex;
         align-items: center;
         gap: var(--nxt-gutter-small);

         .company-logo {
            width: 26px;
            height: 26px;
            border-radius: 50%;
            object-fit: cover;
         }
      }
   }
}
</style>
